import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
// Plugins
import { Observable, Subject, of, shareReplay } from 'rxjs';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import moment from 'moment';
// Models
import { CreateAccountDTO, EmailServiceSaveDTO, GraphDTO, UpdatePaywallAccountPackageDTO } from '@models/account/account.model.dto';
import { 
	AccountRO, AccountsRO, CountriesListRO, CreateAccountRO, GraphRO, ModifyAccountRO, 
	PaywallAccountsRO, TimezoneRO, UpdatePaywallAccountPackageRO, UploadScheduleRO 
} from '@models/account/account.model.ro';
import { 
	Account, AccountFormControls, AccountWarnings, AccountWarningsCollected, 
	CustomerType, EmailServiceControls, PaywallAccountsRequestParams 
} from '@models/account/account.model';
import { PaywallPackage } from '@models/payment/payment.model';
// Shared
import { APP_HOST, ENDPOINTS } from '@shared/endpoints.enum';
// Services
import { HttpService } from '@services/http/http.service';
import { HelperService } from '@services/helper/helper.service';

@Injectable({
	providedIn: 'root'
})
export class AccountService {

	public accountListPreviousParams: string = '';
	public accountPaywallPackages$ = new Subject<Array<PaywallPackage>>();
	private timezones$: Observable<TimezoneRO> = null;
	private countries$: Observable<CountriesListRO> = null;

	public constructor(
		private httpServcice: HttpService,
		private helperService: HelperService
	) { }

	public getAccounts(params: Partial<NzTableQueryParams> = null): Observable<AccountsRO> {
		let httpParams: HttpParams = new HttpParams();

		if (params?.pageIndex) httpParams = httpParams.append('offset', params.pageSize * (params.pageIndex - 1));

		if (params?.pageSize) httpParams = httpParams.append('limit', params.pageSize);

		if (params?.['searchValue']) httpParams = httpParams.append('search', params['searchValue']);

		if (params?.['warning']) httpParams = httpParams.append('warning_filter', params['warning']);

		if (params?.filter?.length && params?.filter?.[0]?.value) {
			httpParams = httpParams.append('type', params?.filter?.[0]?.value);
		}

		if (params?.sort?.length && params?.sort?.[0]?.value) {
			let sortType: number = -1;

			if (params.sort[0].value == 'ascend') sortType = 1;

			httpParams = httpParams.append('sort_field', 'fName');
			httpParams = httpParams.append('sort_type', sortType);
		}

		return this.httpServcice.request('get', ENDPOINTS.PARENTS, APP_HOST.PY, { params: httpParams }, false, true);
	}

	public getAccount(uuid: string): Observable<AccountRO> {
		return this.httpServcice.request('get', `parents/${ uuid }`, APP_HOST.PHP_LARAVEL, false, true);
	}

	public getTimezones(): Observable<TimezoneRO> {
		if (!this.timezones$) {
			this.timezones$ = this.httpServcice.request<null, TimezoneRO>(
				'get', ENDPOINTS.PARENT_TIMEZONES, APP_HOST.PHP
			).pipe(shareReplay(1));
		}

		return this.timezones$;
	}

	public getCountries(): Observable<CountriesListRO> {
		if (!this.countries$) {
			this.countries$ = this.httpServcice.request<null, CountriesListRO>(
				'get', ENDPOINTS.COUNTRIES_LIST, APP_HOST.PHP
			).pipe(shareReplay(1));
		}

		return this.countries$;
	}

	public createAccount(formControls: AccountFormControls): Observable<CreateAccountRO> {
		let data: CreateAccountDTO = {
			name: formControls.name.value,
			address: formControls.address.value,
			timezoneName: formControls.timezone.value,
			customerType: formControls.customerType.value,
			state: formControls.state.value,
			city: formControls.city.value,
			postalCode: formControls.postalCode.value,
			countryCode: formControls.countryCode.value
		};

		return this.httpServcice.request('post', 'parents', APP_HOST.PHP_LARAVEL, data, false, false, true);
	}

	public modifyAccount(formControls: AccountFormControls, accountServerData: Account, deleted: number): Observable<ModifyAccountRO> {
		let customerTypeChangeDate: Date = formControls.customerTypeChangeDate.value;
		let customerFirstInvoiceDate: Date = formControls.customerFirstInvoiceDate.value;
		let customerNextRenewalDate: Date = formControls.customerNextRenewalDate.value;
		let data: Account = {
			...accountServerData,
			fName: formControls.name.value,
			fCustomerType: formControls.customerType.value,
			fAddress: formControls.address.value,
			fCustomerTypeTs: customerTypeChangeDate ? this.helperService.getDateWithLocalTime(customerTypeChangeDate?.getTime()) : null,
			fFirstInvoiceDateTs: customerFirstInvoiceDate ? this.helperService.getDateWithLocalTime(customerFirstInvoiceDate?.getTime()) : null,
			fNextRenewalDateTs: customerNextRenewalDate ? this.helperService.getDateWithLocalTime(customerNextRenewalDate?.getTime()) : null,
			timezone_name: formControls.timezone.value,
			fDeleted: deleted
		};

		return this.httpServcice.request('post', ENDPOINTS.MODIFY_PARENT, APP_HOST.PHP, data, false, false, true);
	}

	public getGraphData(uuid: string, period: string): Observable<GraphRO> {
		let data: GraphDTO = {
			parent_uuids: [uuid],
			period
		};

		return this.httpServcice.request('post', ENDPOINTS.KPI_JOB_CHATS_CREATED_DAY, APP_HOST.PHP, data);
	}

	public saveEmailService(formControls: EmailServiceControls, accountServerData: Account): Observable<ModifyAccountRO> {
		let data: EmailServiceSaveDTO = {
			...accountServerData,
			email_key: formControls.emailKey.value,
			email_user_admin_uuid: formControls.admin.value
		};

		return this.httpServcice.request('post', ENDPOINTS.MODIFY_PARENT, APP_HOST.PHP, data, false, false, true);
	}

	public getCustomerTypes(): Array<CustomerType> {
		return [
			{ value: 'paying_customer', text: 'Customer' },
			{ value: 'sales_trial', text: 'Sales Trial' },
			{ value: 'churn', text: 'Churn' },
			{ value: 'test', text: 'Test' },
			{ value: 'investor_probono', text: 'Investor / Pro Bono' },
			{ value: '0', text: 'Uncategorised' }
		];
	}

	public getCollectedAccountWarnings(warnings: AccountWarnings): AccountWarningsCollected {
		return {
			without_email_key: { 
				key: 'without_email_key', 
				name: 'Accounts missing email key', 
				count: warnings?.without_email_key
			},
			without_email_user_admin_uuid: { 
				key: 'without_email_user_admin_uuid', 
				name: 'Accounts missing account email key admin', 
				count: warnings?.without_email_user_admin_uuid
			},
			without_first_invoice_date: { 
				key: 'without_first_invoice_date', 
				name: 'Accounts missing first invoice date', 
				count: warnings?.without_first_invoice_date
			},
			without_parent_admin: { 
				key: 'without_parent_admin', 
				name: 'Accounts missing account administrator', 
				count: warnings?.without_parent_admin
			},
			without_renewal_date: { 
				key: 'without_renewal_date', 
				name: 'Accounts missing renewal date', 
				count: warnings?.without_renewal_date
			},
			past_renewal_date: {
				key: 'past_renewal_date', 
				name: 'Accounts passed renewal date', 
				count: warnings?.past_renewal_date
			}
		};
	}

	public getPaywallAccounts(data: PaywallAccountsRequestParams): Observable<PaywallAccountsRO> {
        let httpParams = new HttpParams();

        httpParams = httpParams.appendAll({
            limit:  data.pageSize,
            offset:  data.pageIndex
        }); 

        if (data.sortField) httpParams = httpParams.append('sort_field', data.sortField);

        if (data.sortOrder) httpParams = httpParams.append('sort_type', data.sortOrder == 'ascend' ? '0' : '-1');

        if (data.search) httpParams = httpParams.append('search', data.search);

		return this.httpServcice.request('get', ENDPOINTS.PARENT_PACK, APP_HOST.PY, { params: httpParams }, false, true);
    }

	public updatePaywallParentPackage(data: UpdatePaywallAccountPackageDTO): Observable<UpdatePaywallAccountPackageRO> {
		return this.httpServcice.request('put', ENDPOINTS.PARENT_PACK, APP_HOST.PY, data, true, false, true);
    }

	public uploadSchedlue(file: File): Observable<UploadScheduleRO> {
		return this.httpServcice.request('post', ENDPOINTS.SCHEDULE_UPLOAD, APP_HOST.PY, { schedule_excel: file }, true, false);
	}
}